import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { AuthService } from "../../app/auth/auth.service";
import { mapCompanyResourceSummaries } from "../../mappers/resource-management/licensed-clinician-mapper";
import { LookupValue } from "../../models/common/lookup-value";
import ContentWrapper from "../../models/content-wrapper/content-wrapper";
import SortOptions from "../../models/content-wrapper/sort-options";
import CompanyResourceSummary from "../../models/resource-management/company-resource-summary";
import { ConfigurationService } from "../configuration/configuration.service";
import { OrderDataService } from "../order-data-service/order-data-service";

/*
  Generated class for the VolunteerManagementServiceProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class VolunteerManagementServiceProvider {
  public idgDataUrl: string;
  public resourceManagementUrl: string;
  public patientBaseUrl: string;
  public vmBaseUrl: string;
  public signatureBaseUrl: string;
  public headers: HttpHeaders;
  public isVolunteer: boolean = false;
  public isVolunteerAdmin: boolean = false;
  public companyId = 0;
  selectedOrgType: string | undefined;

  constructor(
    public http: HttpClient,
    private orderDataService: OrderDataService,
    private config: ConfigurationService,
    private authSvc: AuthService
  ) {
    this.patientBaseUrl = config.getConfig().apiUrl;
    this.vmBaseUrl = config.getConfig().volunteerManagementUrl;
    this.signatureBaseUrl = config.getConfig().signatureUrl;
    if (this.config.getConfig().isLocal) {
      this.resourceManagementUrl =
        config.getConfig().localEndpoints.resourceManagement;
    } else {
      this.resourceManagementUrl = config.getConfig().apiUrl;
    }
    this.idgDataUrl = config.resolveIdgUrl();
    this.headers = new HttpHeaders().set("Content-Type", "application/json");
  }

  /**
   * Returns a list of company resource summaries as specified by the search
   * @param page the page to pull results on
   * @param limit the number of results to return
   * @param query the query to filter the results by
   * @param sort the column/direction of the results
   * @param skillsInterests
   * @param locationPreferences
   * @param languages
   */
  public getVolunteerSummaries(
    page: number = 0,
    limit: number = 10,
    query: string = "",
    sort: SortOptions,
    skillsInterests: string[] = [],
    locationPreferences: string[] = [],
    languages: number[] = []
  ): Observable<ContentWrapper<CompanyResourceSummary>> {
    let si = skillsInterests.join(",");
    let lp = locationPreferences.join(",");
    let ln = languages.join(",");
    return this.http
      .get<ContentWrapper<CompanyResourceSummary>>(
        `${this.resourceManagementUrl}/company-resources/volunteers?q=${query}&page=${page}&limit=${limit}&sort=${sort.column}&order=${sort.direction}&skills-interests=${si}&location-preferences=${lp}&languages=${ln}`
      )
      .map((x) => {
        return mapCompanyResourceSummaries(x);
      });
  }

  /**
   * Returns a list of company resource summaries as specified by the search
   * @param page the page to pull results on
   * @param limit the number of results to return
   * @param query the query to filter the results by
   * @param sort the column/direction of the results
   * @param skillsInterests
   * @param locationPreferences
   * @param languages
   * @param mustField  filters in Workforce RM
   * @param location
   * @param professionalRelation
   * @param showInactiveUsers
   */
  public getWorkforceVolunteersFilter(
    page: number = 0,
    limit: number = 10,
    query: string = "",
    sort: SortOptions,
    skillsInterests: string[] = [],
    locationPreferences: string = "",
    languages: number[] = [],
    mustField: boolean,
    location: string,
    professionalRelation: LookupValue,
    showInactiveUsers: boolean = false
  ): Observable<ContentWrapper<CompanyResourceSummary>> {
    let si = skillsInterests.join(",");
    let ln = languages.join(",");
    let url =
      `${this.resourceManagementUrl}/company-resources/volunteers` +
      `?q=${query}&page=${page}&limit=${limit}&sort=${sort.column}&order=${sort.direction}` +
      `&skills-interests=${si}&location-preferences=${locationPreferences}&languages=${ln}` +
      `&mustField.nonCredential=${mustField}`;

    if (location !== "") {
      url += `&mustField.location=${location}`;
    }

    if (professionalRelation && professionalRelation.id) {
      url += `&mustField.professionalRelation=${professionalRelation.id}`;
    }
    if (showInactiveUsers) {
      url += `&mustField.inactiveUsers=${showInactiveUsers}`;
    }
    this.validateUserToken();
    if (this.companyId > 0) {
      url += `&companyId=${this.companyId}`;
    }

    return this.http
      .get<ContentWrapper<CompanyResourceSummary>>(url)
      .map((x) => {
        return mapCompanyResourceSummaries(x);
      });
  }

  public getVolunteerNames(searchTerm): Observable<any> {
    return new Observable((observer) => {
      this.http
        .get(
          `${this.idgDataUrl}/company-resources/persons/role/13?size=9999&search=${searchTerm}&strict-to-disciplines=true`,
          { headers: this.headers }
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.orderDataService.presentErrorToast(
              err,
              "Failed to load Volunteer Names."
            );
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getPatientNames(searchTerm): Observable<any> {
    return new Observable((observer) => {
      this.http
        .get(
          `${this.patientBaseUrl}/patient-charts?limit=9999&q=${searchTerm}&status=All&eligibility=ALL`,
          { headers: this.headers }
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.orderDataService.presentErrorToast(
              err,
              "Failed to load Patient Names."
            );
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getActivityCategories() {
    return new Observable((observer) => {
      this.http
        .get(
          `${this.vmBaseUrl}/volunteer-management/volunteer-activities/lookups/activity-categories`,
          { headers: this.headers }
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.orderDataService.presentErrorToast(
              err,
              "Failed to load activity categories."
            );
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  createEleSignature(data) {
    return new Observable((observer) => {
      this.http.post(`${this.signatureBaseUrl}/signatures/`, data).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  deleteEleSignature(signatureId: number) {
    let url: string = `${this.signatureBaseUrl}/signatures/${signatureId}`;
    return new Observable((observer) => {
      this.http.delete(url, { headers: this.headers }).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  createVolunteerActivity(data) {
    return new Observable((observer) => {
      this.http
        .post(
          `${this.vmBaseUrl}/volunteer-management/volunteer-activities`,
          data
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getAllVolunteerActivities(
    page,
    direction,
    sortBy,
    viewAll,
    size = 10
  ) {
    return new Observable((observer) => {
      this.http
        .get(
          `${this.vmBaseUrl}/volunteer-management/volunteer-activities/history?view-all=${viewAll}&page=${page}&size=${size}&sortBy=${sortBy}&direction=${direction}`,
          { headers: this.headers }
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.orderDataService.presentErrorToast(
              err,
              "Failed to load Volunteer Activites."
            );
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getElectSignature(signatureId: number): Observable<any> {
    return new Observable((observer) => {
      this.http
        .get(`${this.signatureBaseUrl}/signatures/${signatureId}`, {
          headers: this.headers,
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  editVolunteerActivity(data, activityId) {
    let url = `${this.vmBaseUrl}/volunteer-management/volunteer-activities/${activityId}`;
    return new Observable((observer) => {
      this.http.put(url, data, { headers: this.headers }).subscribe(
        (response) => {
          observer.next(response);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  deleteVolActivity(id: number) {
    let url: string = `${this.vmBaseUrl}/volunteer-management/volunteer-activities/${id}`;
    return new Observable((observer) => {
      this.http.delete(url, { headers: this.headers }).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  public validateUserToken() {
    this.companyId = 0;
    this.selectedOrgType = this.authSvc.getUser().selectedOrgType;
    if (this.selectedOrgType && this.selectedOrgType === "COMPANY") {
      this.companyId = +this.authSvc.getCompany().companyId;
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/volunteer-management-service/volunteer-management-service.ts